<template>
  <div class="panel-card" :class="{ 'media-only-page': isMediaOnlyRoute }">
    <div class="page-header">
      <h3 class="page-header-title">{{ pageHeaderTitle }}</h3>
    </div>

    <el-tabs v-model="activeMainTab" :class="{ 'is-media-only-main-tabs': isMediaOnlyRoute }">
      <el-tab-pane label="基础设置" name="basic">
        <el-form :model="settings" label-width="140px" class="settings-form">
          <el-form-item label="资质图片指令">
            <el-input v-model="settings.qualification_image_command" placeholder="请输入触发发送资质图片的指令" />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="AI设置" name="ai">
        <el-form :model="settings" label-width="140px" class="settings-form">
          <el-form-item label="DeepSeek API密钥">
            <el-input v-model="settings.deepseek_api_key" placeholder="请输入 DeepSeek API 密钥" />
          </el-form-item>
          <el-form-item label="AI系统提示词">
            <el-input v-model="settings.ai_system_prompt" type="textarea" :rows="8" placeholder="请输入 AI 系统提示词" />
          </el-form-item>
        </el-form>
      </el-tab-pane>

      <el-tab-pane label="开场话术" name="opening-prompts">
        <div class="opening-toolbar">
          <el-button type="primary" size="mini" @click="openOpeningPromptDialog()">新增话术</el-button>
          <el-button size="mini" @click="loadOpeningPrompts">刷新</el-button>
        </div>

        <el-table
          v-loading="openingPromptsLoading"
          :data="openingPrompts"
          stripe
          border
          class="opening-prompt-table"
          empty-text="暂无开场话术"
        >
          <el-table-column type="index" width="56" label="#" />
          <el-table-column label="标题" min-width="180">
            <template slot-scope="{ row }">
              <span>{{ row.title || "-" }}</span>
            </template>
          </el-table-column>
          <el-table-column label="类型" min-width="160">
            <template slot-scope="{ row }">
              <span>{{ resolveOpeningPromptTypeLabel(row.type) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="启用" width="100" align="center">
            <template slot-scope="{ row }">
              <el-switch :value="Boolean(row.enabled)" @change="toggleOpeningPromptEnabled(row, $event)" />
            </template>
          </el-table-column>
          <el-table-column label="排序" width="90" align="center">
            <template slot-scope="{ row }">{{ row.sort_order || 0 }}</template>
          </el-table-column>
          <el-table-column label="话术内容" min-width="420">
            <template slot-scope="{ row }">
              <div class="opening-prompt-content">{{ row.content || "-" }}</div>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="mini" @click="openOpeningPromptDialog(row)">编辑</el-button>
              <el-button type="text" size="mini" class="danger" @click="removeOpeningPrompt(row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>

      <el-tab-pane v-if="isMediaOnlyRoute" label="图片素材库" name="media">
        <el-tabs v-model="activeMediaCategory" @tab-click="handleMediaTabChange">
          <el-tab-pane v-for="category in mediaCategories" :key="category.key" :label="category.label" :name="category.key">
            <div class="media-toolbar">
              <div class="media-toolbar-left">
                <el-button type="primary" size="mini" @click="openUploadDialog">新增图片</el-button>
                <el-button size="mini" @click="openCreateFolderDialog">新建文件夹</el-button>
                <el-button size="mini" @click="reloadCurrentCategory">刷新</el-button>
                <el-button size="mini" @click="exportMediaLinks">导出链接</el-button>
              </div>
              <div class="media-toolbar-right">
                <span class="media-count">文件夹 {{ activeMediaState.folders.length }} 个</span>
                <span class="media-count">图片 {{ activeMediaState.images.length }} 张</span>
              </div>
            </div>

            <el-alert
              v-if="isClosedCaseCategory"
              title="已判离案例图片：请先进入文件夹后再批量上传图片，标签在文件夹内维护。"
              type="info"
              :closable="false"
              class="media-tip"
            />

            <div class="media-breadcrumb-wrap">
              <el-breadcrumb separator="/">
                <el-breadcrumb-item v-for="(crumb, idx) in activeMediaState.breadcrumbs" :key="`crumb-${idx}-${crumb.id || 'root'}`">
                  <a href="javascript:void(0)" @click.prevent="goToBreadcrumb(crumb.id)">{{ crumb.name }}</a>
                </el-breadcrumb-item>
              </el-breadcrumb>
            </div>

            <div v-loading="activeMediaState.loading">
              <div class="section-title">文件夹</div>
              <div class="folder-grid">
                <div v-for="folder in activeMediaState.folders" :key="`folder-${folder.id}`" class="folder-card" @dblclick="openFolder(folder)">
                  <div class="folder-head">
                    <i class="el-icon-folder folder-icon"></i>
                    <span class="folder-name" :title="folder.name">{{ folder.name }}</span>
                  </div>
                  <div class="folder-tags" v-if="folder.tags && folder.tags.length">
                    <el-tag v-for="tag in folder.tags" :key="`folder-tag-${folder.id}-${tag}`" size="mini" type="info">{{ tag }}</el-tag>
                  </div>
                  <div class="folder-meta" v-if="isClosedCaseCategory">{{ formatClosedCaseFolderMeta(folder) }}</div>
                  <div class="folder-actions">
                    <el-button type="text" size="mini" @click.stop="openFolder(folder)">打开</el-button>
                    <el-button v-if="isClosedCaseCategory" type="text" size="mini" @click.stop="openEditFolderDialog(folder)">编辑</el-button>
                    <el-button type="text" size="mini" class="danger" @click.stop="removeFolder(folder)">删除</el-button>
                  </div>
                </div>
                <div v-if="!activeMediaState.folders.length" class="folder-empty">当前目录暂无文件夹</div>
              </div>

              <div class="section-title">数据台账</div>
              <el-table :data="activeMediaState.images" stripe border class="media-ledger-table" :empty-text="activeMediaState.loading ? '加载中...' : '当前目录暂无图片'">
                <el-table-column type="index" width="56" label="#" />
                <el-table-column label="预览" width="110">
                  <template slot-scope="{ row }">
                    <el-image v-if="row.file_url" class="ledger-thumb" :src="row.file_url" :preview-src-list="[row.file_url]" fit="cover" />
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column label="文件名" min-width="220">
                  <template slot-scope="{ row }">
                    <div class="file-title">{{ row.file_name || '-' }}</div>
                    <div class="file-name-sub" v-if="row.title">{{ row.title }}</div>
                  </template>
                </el-table-column>
                <el-table-column label="标签" min-width="180">
                  <template slot-scope="{ row }">
                    <div class="tag-wrap" v-if="resolveRowTags(row).length">
                      <el-tag v-for="tag in resolveRowTags(row)" :key="`image-tag-${row.id}-${tag}`" size="mini">{{ tag }}</el-tag>
                    </div>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column label="分类信息" min-width="260">
                  <template slot-scope="{ row }">{{ formatImageMetaSummary(row) }}</template>
                </el-table-column>
                <el-table-column label="大小" width="100">
                  <template slot-scope="{ row }">{{ formatFileSize(row.file_size) }}</template>
                </el-table-column>
                <el-table-column label="上传时间" min-width="170">
                  <template slot-scope="{ row }">{{ row.created_at || '-' }}</template>
                </el-table-column>
                <el-table-column label="H5链接" min-width="220">
                  <template slot-scope="{ row }">
                    <div v-if="normalizeRowH5Links(row).length" class="h5-link-wrap">
                      <a
                        v-for="(link, idx) in normalizeRowH5Links(row)"
                        :key="`h5-link-${row.id}-${idx}`"
                        class="h5-link-item"
                        :href="link.url"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{ link.label || link.url }}
                      </a>
                    </div>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="170" fixed="right">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="mini" @click="previewImage(row)">查看</el-button>
                    <el-button type="text" size="mini" class="danger" @click="removeImage(row)">删除</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-tab-pane>
    </el-tabs>

    <div v-if="!isMediaOnlyRoute" class="footer-actions">
      <el-button type="primary" :loading="settingsSaving" @click="saveSettings">保存设置</el-button>
    </div>

    <el-dialog
      :title="openingPromptDialogMode === 'create' ? '新增开场话术' : '编辑开场话术'"
      :visible.sync="openingPromptDialogVisible"
      width="640px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
      @close="resetOpeningPromptForm"
    >
      <el-form :model="openingPromptForm" label-width="90px">
        <el-form-item label="标题">
          <el-input v-model="openingPromptForm.title" maxlength="80" placeholder="请输入话术标题" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select
            v-model="openingPromptForm.type"
            filterable
            allow-create
            default-first-option
            placeholder="请选择或输入话术类型"
            style="width: 100%"
          >
            <el-option
              v-for="option in openingPromptTypeOptions"
              :key="`opening-prompt-type-${option.value}`"
              :label="option.label"
              :value="option.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="openingPromptForm.sort_order" :min="1" :max="999" controls-position="right" />
        </el-form-item>
        <el-form-item label="启用">
          <el-switch v-model="openingPromptForm.enabled" />
        </el-form-item>
        <el-form-item label="话术内容">
          <el-input
            v-model="openingPromptForm.content"
            type="textarea"
            :rows="6"
            placeholder="请输入开场话术内容"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="openingPromptDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="openingPromptSaving" @click="submitOpeningPrompt">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="新建文件夹"
      :visible.sync="createFolderDialogVisible"
      width="460px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
      @close="resetCreateFolderForm"
    >
      <el-form :model="createFolderForm" label-width="90px">
        <el-form-item label="文件夹名称">
          <el-input v-model="createFolderForm.name" maxlength="120" placeholder="请输入文件夹名称" />
        </el-form-item>
        <el-form-item v-if="!isClosedCaseCategory" label="标签">
          <el-input v-model="createFolderForm.tags" placeholder="多个标签用逗号分隔" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="createFolderDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="creatingFolder" @click="submitCreateFolder">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="编辑文件夹"
      :visible.sync="editFolderDialogVisible"
      width="460px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
      @close="resetEditFolderForm"
    >
      <el-form :model="editFolderForm" label-width="90px">
        <el-form-item label="文件夹名称">
          <el-input v-model="editFolderForm.name" maxlength="120" placeholder="请输入文件夹名称" />
        </el-form-item>
        <el-form-item label="标签">
          <el-input v-model="editFolderForm.tags" placeholder="多个标签用逗号分隔" />
        </el-form-item>
        <template v-if="isClosedCaseCategory">
          <el-form-item label="客户名称">
            <el-input v-model="editFolderForm.client_name" placeholder="可选，用于区分客户" />
          </el-form-item>
          <el-form-item label="离婚类型">
            <el-select v-model="editFolderForm.divorce_type" placeholder="请选择离婚类型" style="width: 100%">
              <el-option v-for="type in activeMediaState.constants.divorce_types" :key="`edit-folder-divorce-type-${type}`" :label="type" :value="type" />
            </el-select>
          </el-form-item>
          <el-form-item label="省份">
            <el-select v-model="editFolderForm.province" filterable placeholder="请选择省份" style="width: 100%">
              <el-option v-for="province in provinceOptions" :key="`edit-folder-province-${province}`" :label="province" :value="province" />
            </el-select>
          </el-form-item>
        </template>
      </el-form>
      <div slot="footer">
        <el-button @click="editFolderDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="editingFolder" @click="submitEditFolder">确定</el-button>
      </div>
    </el-dialog>

    <el-dialog
      title="新增图片"
      :visible.sync="uploadDialogVisible"
      width="640px"
      :modal="true"
      :append-to-body="true"
      :modal-append-to-body="true"
      :lock-scroll="true"
      @close="resetUploadForm"
    >
      <el-form :model="uploadForm" label-width="100px">
        <el-form-item label="上传图片">
          <el-upload
            ref="settingMediaUpload"
            class="upload-box"
            drag
            action="#"
            :auto-upload="false"
            multiple
            accept="image/*"
            :limit="30"
            :file-list="uploadFileList"
            :on-change="handleUploadChange"
            :on-remove="handleUploadRemove"
            :on-exceed="handleUploadExceed"
          >
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">将图片拖到此处，或 <em>点击上传</em></div>
            <div class="el-upload__tip">支持多图上传，单次最多30张，单张不超过10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="uploadDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="uploadingImage" @click="submitUploadImage">上传</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { settingsApi } from "../../api";
import { PROVINCE_OPTIONS } from "../../constants/chinaRegions";

const MEDIA_CATEGORIES = [
  { key: "closed_case", label: "已经判离案例图片" },
  { key: "praise", label: "判离好评图片" },
  { key: "customer_region", label: "客户地区图片" },
  { key: "divorce_reason", label: "离婚原因图片" },
  { key: "payment_qr", label: "\u4ed8\u6b3e\u7801" },
  { key: "lawyer_qualification", label: "律所资质材料" },
  { key: "online_hearing", label: "线上开庭" }
];

const DEFAULT_DIVORCE_TYPES = ["协议离婚", "诉讼离婚", "调解离婚", "二次起诉", "涉外离婚", "其他"];
const DEFAULT_PRAISE_TYPES = ["好评截图", "好评锦旗"];
const DEFAULT_REASON_TYPES = [
  "不上进", "出轨", "赌博", "对方不同意", "对方起诉", "对家庭不管不顾", "反悔",
  "夫妻生活", "负债", "感情不和", "画大饼", "家暴", "经常吵架", "军婚自己是军人",
  "恐吓", "冷暴力", "聊骚", "妈宝男", "骗婚", "嫖娼", "婆媳矛盾", "三观不合",
  "杀我", "杀我全家", "闪婚", "跳楼", "威胁", "吸毒", "性格不和", "酗酒",
  "隐瞒疾病", "自杀", "坐牢"
];

const DEFAULT_OPENING_PROMPT_TYPE_OPTIONS = [
  { value: "generic_opening", label: "通用开场" },
  { value: "initial_greeting", label: "首轮问候" },
  { value: "name_gender_confirm", label: "称呼身份确认" },
  { value: "stage_selector", label: "阶段确认" },
  { value: "delegated_consultation", label: "代人咨询确认" }
];

function createEmptyMediaState() {
  return {
    loading: false,
    currentFolderId: null,
    currentFolder: null,
    breadcrumbs: [],
    folders: [],
    images: [],
    constants: {
      divorce_types: DEFAULT_DIVORCE_TYPES.slice(),
      praise_types: DEFAULT_PRAISE_TYPES.slice(),
      reason_types: DEFAULT_REASON_TYPES.slice()
    }
  };
}

export default {
  name: "Settings",
  data() {
    return {
      activeMainTab: "basic",
      settings: {
        qualification_image_command: "",
        deepseek_api_key: "",
        ai_system_prompt: ""
      },
      settingsSaving: false,
      openingPrompts: [],
      openingPromptTypeOptions: DEFAULT_OPENING_PROMPT_TYPE_OPTIONS.slice(),
      openingPromptsLoading: false,
      openingPromptDialogVisible: false,
      openingPromptDialogMode: "create",
      openingPromptSaving: false,
      openingPromptForm: {
        id: null,
        title: "",
        type: "generic_opening",
        content: "",
        enabled: true,
        sort_order: 1
      },

      mediaCategories: MEDIA_CATEGORIES,
      activeMediaCategory: MEDIA_CATEGORIES[0].key,
      mediaStore: {},

      createFolderDialogVisible: false,
      createFolderForm: {
        name: "",
        tags: "",
        client_name: "",
        divorce_type: "",
        province: ""
      },
      creatingFolder: false,

      editFolderDialogVisible: false,
      editFolderForm: {
        id: null,
        name: "",
        tags: "",
        client_name: "",
        divorce_type: "",
        province: ""
      },
      editingFolder: false,

      uploadDialogVisible: false,
      uploadForm: {},
      uploadFileList: [],
      uploadFilesRaw: [],
      uploadingImage: false
    };
  },
  computed: {
    isMediaOnlyRoute() {
      return this.$route && this.$route.path === "/media-library";
    },
    pageHeaderTitle() {
      return this.isMediaOnlyRoute ? "图片素材库" : "系统设置";
    },
    activeMediaState() {
      return this.mediaStore[this.activeMediaCategory] || createEmptyMediaState();
    },
    provinceOptions() {
      return PROVINCE_OPTIONS;
    },
    isClosedCaseCategory() {
      return this.activeMediaCategory === "closed_case";
    },
    isPraiseCategory() {
      return this.activeMediaCategory === "praise";
    },
    isCustomerRegionCategory() {
      return this.activeMediaCategory === "customer_region";
    },
    isDivorceReasonCategory() {
      return this.activeMediaCategory === "divorce_reason";
    }
  },
  watch: {
    "$route.path"() {
      this.applyRouteMode();
    }
  },
  mounted() {
    this.initializeMediaStore();
    this.applyRouteMode();
  },
  methods: {
    applyRouteMode() {
      if (this.isMediaOnlyRoute) {
        this.activeMainTab = "media";
        this.loadMediaLibrary(this.activeMediaCategory, null);
        return;
      }
      if (this.activeMainTab === "media") {
        this.activeMainTab = "basic";
      }
      this.loadSettings();
      this.loadOpeningPrompts();
    },
    initializeMediaStore() {
      const stateMap = {};
      this.mediaCategories.forEach((item) => {
        stateMap[item.key] = createEmptyMediaState();
      });
      this.mediaStore = stateMap;
    },
    ensureMediaState(category) {
      if (!this.mediaStore[category]) {
        this.$set(this.mediaStore, category, createEmptyMediaState());
      }
      return this.mediaStore[category];
    },
    async loadSettings() {
      try {
        const settings = await settingsApi.getSettings();
        if (Array.isArray(settings)) {
          settings.forEach((item) => {
            if (!item || !item.key) return;
            this.$set(this.settings, item.key, item.value);
          });
        }
      } catch (error) {
        this.$message.error("加载系统设置失败");
      }
    },
    async loadOpeningPrompts() {
      this.openingPromptsLoading = true;
      try {
        const payload = await settingsApi.getOpeningPrompts();
        const items = Array.isArray(payload) ? payload : (Array.isArray(payload && payload.items) ? payload.items : []);
        const typeOptions = Array.isArray(payload && payload.type_options) ? payload.type_options : [];
        this.openingPrompts = items;
        this.openingPromptTypeOptions = this.normalizeOpeningPromptTypeOptions(typeOptions, items);
      } catch (error) {
        this.$message.error("加载开场话术失败");
      } finally {
        this.openingPromptsLoading = false;
      }
    },
    async saveSettings() {
      if (this.settingsSaving) return;
      this.settingsSaving = true;
      try {
        const keys = ["qualification_image_command", "deepseek_api_key", "ai_system_prompt"];
        for (const key of keys) {
          await settingsApi.updateSetting(key, { value: this.settings[key] });
        }
        this.$message.success("系统设置保存成功");
      } catch (error) {
        this.$message.error("系统设置保存失败");
      } finally {
        this.settingsSaving = false;
      }
    },
    openOpeningPromptDialog(row = null) {
      if (row && row.id) {
        this.openingPromptDialogMode = "edit";
        this.openingPromptForm = {
          id: row.id,
          title: row.title || "",
          type: row.type || "generic_opening",
          content: row.content || "",
          enabled: Boolean(row.enabled),
          sort_order: Number(row.sort_order || 1)
        };
      } else {
        this.openingPromptDialogMode = "create";
        this.resetOpeningPromptForm();
      }
      this.openingPromptDialogVisible = true;
    },
    resetOpeningPromptForm() {
      this.openingPromptSaving = false;
      this.openingPromptForm = {
        id: null,
        title: "",
        type: "generic_opening",
        content: "",
        enabled: true,
        sort_order: this.openingPrompts.length + 1
      };
    },
    async submitOpeningPrompt() {
      if (this.openingPromptSaving) return;

      const title = String(this.openingPromptForm.title || "").trim();
      const type = String(this.openingPromptForm.type || "").trim() || "generic_opening";
      const content = String(this.openingPromptForm.content || "").trim();
      if (!title) {
        this.$message.warning("请输入话术标题");
        return;
      }
      if (!content) {
        this.$message.warning("请输入话术内容");
        return;
      }

      const payload = {
        title,
        type,
        content,
        enabled: Boolean(this.openingPromptForm.enabled),
        sort_order: Number(this.openingPromptForm.sort_order || 1)
      };

      this.openingPromptSaving = true;
      try {
        if (this.openingPromptDialogMode === "edit" && this.openingPromptForm.id) {
          await settingsApi.updateOpeningPrompt(this.openingPromptForm.id, payload);
          this.$message.success("开场话术更新成功");
        } else {
          await settingsApi.createOpeningPrompt(payload);
          this.$message.success("开场话术新增成功");
        }
        this.openingPromptDialogVisible = false;
        await this.loadOpeningPrompts();
      } catch (error) {
        this.$message.error(this.openingPromptDialogMode === "edit" ? "开场话术更新失败" : "开场话术新增失败");
      } finally {
        this.openingPromptSaving = false;
      }
    },
    async toggleOpeningPromptEnabled(row, enabled) {
      if (!row || !row.id) return;
      try {
        await settingsApi.updateOpeningPrompt(row.id, { enabled });
        row.enabled = Boolean(enabled);
        this.$message.success("开场话术状态已更新");
      } catch (error) {
        this.$message.error("开场话术状态更新失败");
        await this.loadOpeningPrompts();
      }
    },
    async removeOpeningPrompt(row) {
      if (!row || !row.id) return;
      try {
        await this.$confirm(`确定删除开场话术「${row.title}」吗？`, "提示", { type: "warning" });
      } catch (e) {
        return;
      }

      try {
        await settingsApi.deleteOpeningPrompt(row.id);
        this.$message.success("开场话术删除成功");
        await this.loadOpeningPrompts();
      } catch (error) {
        this.$message.error("开场话术删除失败");
      }
    },
    normalizeOpeningPromptTypeOptions(typeOptions, items = []) {
      const options = Array.isArray(typeOptions) && typeOptions.length
        ? typeOptions
        : DEFAULT_OPENING_PROMPT_TYPE_OPTIONS;

      const map = new Map();
      options.forEach((option) => {
        if (!option) return;
        const value = String(option.value || "").trim();
        if (!value) return;
        map.set(value, {
          value,
          label: String(option.label || value).trim() || value
        });
      });

      (Array.isArray(items) ? items : []).forEach((item) => {
        const value = String(item && item.type ? item.type : "").trim();
        if (!value || map.has(value)) return;
        map.set(value, { value, label: value });
      });

      return Array.from(map.values());
    },
    resolveOpeningPromptTypeLabel(type) {
      const normalized = String(type || "").trim() || "generic_opening";
      const matched = this.openingPromptTypeOptions.find((option) => option.value === normalized);
      return matched && matched.label ? matched.label : normalized;
    },
    async handleMediaTabChange() {
      const state = this.ensureMediaState(this.activeMediaCategory);
      if (!state.breadcrumbs.length && !state.folders.length && !state.images.length) {
        await this.loadMediaLibrary(this.activeMediaCategory, null);
      }
    },
    async loadMediaLibrary(category, folderId = undefined) {
      const state = this.ensureMediaState(category);
      const targetFolderId = folderId === undefined ? state.currentFolderId : folderId;
      state.loading = true;
      try {
        const params = { category };
        if (targetFolderId) params.folder_id = targetFolderId;
        const data = await settingsApi.getMediaLibrary(params);

        state.currentFolderId = targetFolderId || null;
        state.currentFolder = data && data.current_folder ? data.current_folder : null;
        state.breadcrumbs = Array.isArray(data && data.breadcrumbs) ? data.breadcrumbs : [];
        state.folders = Array.isArray(data && data.folders) ? data.folders : [];
        state.images = Array.isArray(data && data.images) ? data.images : [];

        const constants = data && data.constants ? data.constants : {};
        state.constants = {
          divorce_types: Array.isArray(constants.divorce_types) && constants.divorce_types.length
            ? constants.divorce_types
            : DEFAULT_DIVORCE_TYPES.slice(),
          praise_types: Array.isArray(constants.praise_types) && constants.praise_types.length
            ? constants.praise_types
            : DEFAULT_PRAISE_TYPES.slice(),
          reason_types: Array.isArray(constants.reason_types) && constants.reason_types.length
            ? constants.reason_types
            : DEFAULT_REASON_TYPES.slice()
        };
      } catch (error) {
        state.currentFolder = null;
        state.breadcrumbs = [];
        state.folders = [];
        state.images = [];
        this.$message.error("加载素材库失败");
      } finally {
        state.loading = false;
      }
    },
    async reloadCurrentCategory() {
      await this.loadMediaLibrary(this.activeMediaCategory, this.activeMediaState.currentFolderId);
    },
    async openFolder(folder) {
      if (!folder || !folder.id) return;
      await this.loadMediaLibrary(this.activeMediaCategory, folder.id);
    },
    async goToBreadcrumb(folderId) {
      await this.loadMediaLibrary(this.activeMediaCategory, folderId || null);
    },
    openCreateFolderDialog() {
      this.resetCreateFolderForm();
      this.createFolderDialogVisible = true;
    },
    resetCreateFolderForm() {
      this.creatingFolder = false;
      this.createFolderForm = {
        name: "",
        tags: "",
        client_name: "",
        divorce_type: "",
        province: ""
      };
    },
    openEditFolderDialog(folder) {
      if (!folder || !folder.id) return;
      const meta = folder.meta && typeof folder.meta === "object" ? folder.meta : {};
      this.editFolderForm = {
        id: folder.id,
        name: folder.name || "",
        tags: Array.isArray(folder.tags) ? folder.tags.join(",") : "",
        client_name: meta.client_name || "",
        divorce_type: meta.divorce_type || "",
        province: meta.province || ""
      };
      this.editFolderDialogVisible = true;
    },
    resetEditFolderForm() {
      this.editingFolder = false;
      this.editFolderForm = {
        id: null,
        name: "",
        tags: "",
        client_name: "",
        divorce_type: "",
        province: ""
      };
    },
    parseTags(text) {
      return String(text || "")
        .split(/[，,]/)
        .map((item) => item.trim())
        .filter(Boolean);
    },
    async submitCreateFolder() {
      if (this.creatingFolder) return;
      const name = String(this.createFolderForm.name || "").trim();
      if (!name) {
        this.$message.warning("请输入文件夹名称");
        return;
      }

      const payload = {
        category: this.activeMediaCategory,
        parent_id: this.activeMediaState.currentFolderId || null,
        name
      };
      if (!this.isClosedCaseCategory) {
        payload.tags = this.parseTags(this.createFolderForm.tags);
      }

      this.creatingFolder = true;
      try {
        await settingsApi.createMediaFolder(payload);
        this.$message.success("文件夹创建成功");
        this.createFolderDialogVisible = false;
        await this.reloadCurrentCategory();
      } catch (error) {
        if (!error || !error.message) this.$message.error("创建文件夹失败");
      } finally {
        this.creatingFolder = false;
      }
    },
    async submitEditFolder() {
      if (this.editingFolder) return;
      const folderId = Number(this.editFolderForm.id || 0);
      if (!folderId) return;

      const name = String(this.editFolderForm.name || "").trim();
      if (!name) {
        this.$message.warning("请输入文件夹名称");
        return;
      }

      const payload = {
        name,
        tags: this.parseTags(this.editFolderForm.tags)
      };

      if (this.isClosedCaseCategory) {
        if (!this.editFolderForm.divorce_type) {
          this.$message.warning("请选择离婚类型");
          return;
        }
        if (!this.editFolderForm.province) {
          this.$message.warning("请选择省份");
          return;
        }
        payload.meta = {
          divorce_type: this.editFolderForm.divorce_type,
          province: this.editFolderForm.province
        };
        const clientName = String(this.editFolderForm.client_name || "").trim();
        if (clientName) payload.meta.client_name = clientName;
      }

      this.editingFolder = true;
      try {
        await settingsApi.updateMediaFolder(folderId, payload);
        this.$message.success("文件夹编辑成功");
        this.editFolderDialogVisible = false;
        await this.reloadCurrentCategory();
      } catch (error) {
        if (!error || !error.message) this.$message.error("文件夹编辑失败");
      } finally {
        this.editingFolder = false;
      }
    },
    async removeFolder(folder) {
      if (!folder || !folder.id) return;
      try {
        await this.$confirm(`确定删除文件夹「${folder.name}」吗？`, "提示", { type: "warning" });
      } catch (e) {
        return;
      }

      try {
        await settingsApi.deleteMediaFolder(folder.id);
        this.$message.success("文件夹删除成功");
        await this.reloadCurrentCategory();
      } catch (error) {
        if (!error || !error.message) this.$message.error("删除文件夹失败");
      }
    },
    async openUploadDialog() {
      this.resetUploadForm();
      this.uploadDialogVisible = true;
    },
    resetUploadForm() {
      this.uploadingImage = false;
      this.uploadForm = {};
      this.uploadFileList = [];
      this.uploadFilesRaw = [];
      const uploadRef = this.$refs.settingMediaUpload;
      if (uploadRef && typeof uploadRef.clearFiles === "function") uploadRef.clearFiles();
    },
    handleUploadChange(file, fileList) {
      const list = Array.isArray(fileList) ? fileList.slice(0, 30) : [];
      this.uploadFileList = list;
      this.uploadFilesRaw = list.map((item) => item && item.raw).filter(Boolean);
    },
    handleUploadRemove(file, fileList) {
      const list = Array.isArray(fileList) ? fileList : [];
      this.uploadFileList = list;
      this.uploadFilesRaw = list.map((item) => item && item.raw).filter(Boolean);
    },
    handleUploadExceed() {
      this.$message.warning("单次最多上传30张图片");
    },
    validateUploadBeforeSubmit() {
      if (!this.uploadFilesRaw.length) {
        this.$message.warning("请先选择要上传的图片");
        return false;
      }
      if (this.isClosedCaseCategory && !this.activeMediaState.currentFolderId) {
        this.$message.warning("已判离案例图片请先进入文件夹后上传");
        return false;
      }
      return true;
    },
    async submitUploadImage() {
      if (this.uploadingImage) return;
      if (!this.validateUploadBeforeSubmit()) return;

      this.uploadingImage = true;
      try {
        const formData = new FormData();
        formData.append("category", this.activeMediaCategory);
        if (this.activeMediaState.currentFolderId) {
          formData.append("folder_id", String(this.activeMediaState.currentFolderId));
        }
        this.uploadFilesRaw.forEach((file) => {
          formData.append("images[]", file, file.name || "image.jpg");
        });

        const result = await settingsApi.uploadMediaImage(formData);
        const count = Number(result && result.count ? result.count : 0);
        this.$message.success(count > 0 ? `上传成功，共 ${count} 张` : "上传成功");
        this.uploadDialogVisible = false;
        this.reloadCurrentCategory();
      } catch (error) {
        if (!error || !error.message) this.$message.error("图片上传失败");
      } finally {
        this.uploadingImage = false;
      }
    },
    async removeImage(image) {
      if (!image || !image.id) return;
      try {
        await this.$confirm("确定删除该图片吗？", "提示", { type: "warning" });
      } catch (e) {
        return;
      }

      try {
        await settingsApi.deleteMediaImage(image.id);
        this.$message.success("图片删除成功");
        await this.reloadCurrentCategory();
      } catch (error) {
        if (!error || !error.message) this.$message.error("图片删除失败");
      }
    },
    previewImage(image) {
      const url = String(image && image.file_url ? image.file_url : "").trim();
      if (!url) {
        this.$message.warning("图片链接不存在");
        return;
      }
      window.open(url, "_blank");
    },
    normalizeRowH5Links(row) {
      const links = Array.isArray(row && row.h5_links) ? row.h5_links : [];
      const normalized = links
        .map((item) => ({
          label: String(item && item.label ? item.label : ""),
          url: String(item && item.url ? item.url : "").trim()
        }))
        .filter((item) => item.url);

      if (normalized.length > 0) {
        return normalized;
      }

      const folderId = Number(row && row.folder_id ? row.folder_id : 0);
      if (this.activeMediaCategory === "closed_case" && Number.isFinite(folderId) && folderId > 0) {
        const origin = typeof window !== "undefined" && window.location ? window.location.origin : "";
        const base = origin || "";
        return [{
          label: "已判离案例文件夹",
          url: `${base}/h5/closed-case/${folderId}`
        }];
      }

      return [];
    },
    async exportMediaLinks() {
      try {
        const blob = await settingsApi.exportMediaLinks({
          category: this.activeMediaCategory,
          share: 1
        });
        const file = blob instanceof Blob
          ? blob
          : new Blob([blob], { type: "text/markdown;charset=utf-8" });
        const fileName = `media-links-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.md`;
        const downloadUrl = URL.createObjectURL(file);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        this.$message.error("导出链接失败");
      }
    },
    resolveRowTags(image) {
      if (this.isClosedCaseCategory) {
        const tags = this.activeMediaState.currentFolder && Array.isArray(this.activeMediaState.currentFolder.tags)
          ? this.activeMediaState.currentFolder.tags
          : [];
        return tags;
      }
      return Array.isArray(image && image.tags) ? image.tags : [];
    },
    formatClosedCaseFolderMeta(folder) {
      const meta = folder && folder.meta && typeof folder.meta === "object" ? folder.meta : {};
      const parts = [];
      if (meta.province) parts.push(`省份: ${meta.province}`);
      if (meta.divorce_type) parts.push(`离婚类型: ${meta.divorce_type}`);
      if (meta.client_name) parts.push(`客户: ${meta.client_name}`);
      return parts.length ? parts.join("；") : "未设置省份/离婚类型";
    },
    formatImageMetaSummary(image) {
      if (this.isClosedCaseCategory) {
        return this.formatClosedCaseFolderMeta(this.activeMediaState.currentFolder);
      }
      const meta = image && image.meta && typeof image.meta === "object" ? image.meta : {};
      if (this.activeMediaCategory === "praise") {
        const parts = [];
        if (meta.praise_type) parts.push(`类型: ${meta.praise_type}`);
        if (meta.sales_personnel_name) parts.push(`销售: ${meta.sales_personnel_name}`);
        else if (meta.sales_personnel_id) parts.push(`销售ID: ${meta.sales_personnel_id}`);
        return parts.length ? parts.join("；") : "-";
      }
      if (this.activeMediaCategory === "customer_region") {
        return meta.province ? `省份: ${meta.province}` : "-";
      }
      if (this.activeMediaCategory === "divorce_reason") {
        return meta.reason_type ? `离婚原因: ${meta.reason_type}` : "-";
      }
      return "-";
    },
    formatFileSize(size) {
      const bytes = Number(size || 0);
      if (!Number.isFinite(bytes) || bytes <= 0) return "-";
      if (bytes < 1024) return `${bytes}B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
    }
  }
};
</script>

<style scoped>
.panel-card { padding: 20px; }
.page-header { margin-bottom: 14px; }
.page-header-title { font-size: 18px; font-weight: 600; margin: 0; }
:deep(.is-media-only-main-tabs > .el-tabs__header) { display: none; }
.settings-form { margin-top: 12px; }
.footer-actions { margin-top: 16px; }
.opening-toolbar { display: flex; align-items: center; gap: 8px; margin: 8px 0 12px; }
.opening-prompt-table { width: 100%; }
.opening-prompt-content { white-space: pre-wrap; line-height: 1.6; color: #334155; }
.media-toolbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 8px 0 12px; }
.media-toolbar-left { display: flex; align-items: center; gap: 8px; }
.media-toolbar-right { display: flex; align-items: center; gap: 10px; }
.media-count { font-size: 12px; color: #64748b; }
.media-tip { margin-bottom: 10px; }
.media-breadcrumb-wrap { padding: 8px 10px; margin-bottom: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
.section-title { margin: 14px 0 10px; font-weight: 600; font-size: 14px; color: #1e293b; }
.folder-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 10px; margin-bottom: 14px; }
.folder-card { border: 1px solid #dbeafe; background: #f8fbff; border-radius: 10px; padding: 10px; }
.folder-head { display: flex; align-items: center; gap: 8px; }
.folder-icon { color: #2563eb; font-size: 20px; }
.folder-name { font-size: 14px; font-weight: 600; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.folder-tags { margin-top: 8px; display: flex; flex-wrap: wrap; gap: 6px; }
.folder-meta { margin-top: 8px; font-size: 12px; color: #64748b; }
.folder-actions { margin-top: 8px; }
.folder-empty { grid-column: 1 / -1; padding: 16px; border: 1px dashed #cbd5e1; border-radius: 10px; color: #64748b; text-align: center; background: #f8fafc; }
.media-ledger-table { width: 100%; }
.ledger-thumb { width: 72px; height: 72px; border-radius: 8px; border: 1px solid #e2e8f0; }
.file-title { color: #0f172a; font-weight: 500; }
.file-name-sub { margin-top: 4px; color: #64748b; font-size: 12px; }
.tag-wrap { display: flex; flex-wrap: wrap; gap: 6px; }
.h5-link-wrap { display: flex; flex-direction: column; gap: 4px; }
.h5-link-item { color: #2563eb; font-size: 12px; text-decoration: none; word-break: break-all; }
.h5-link-item:hover { text-decoration: underline; }
.danger { color: #dc2626; }
.upload-box { width: 100%; }
@media (max-width: 900px) {
  .panel-card { padding: 12px; }
  .media-toolbar { flex-direction: column; align-items: flex-start; }
  .media-toolbar-right { width: 100%; justify-content: flex-start; }
}
</style>
